import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { League, Sport, Country } from '../types/sports';
import LeagueForm from '../components/leagues/LeagueForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Toast from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { Plus, Pencil, Trash2, CheckSquare, XCircle, CheckCircle, LayoutGrid, List, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Leagues management page with full CRUD functionality
 */
const Leagues: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  
  // CRUD states
  const [showForm, setShowForm] = useState(false);
  const [editingLeague, setEditingLeague] = useState<League | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<League | null>(null);
  const [selectedLeagues, setSelectedLeagues] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  
  // Toast
  const { toast, showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadLeagues();
  }, [currentPage, pageSize, selectedSport, selectedCountry]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [sportsData, countriesData] = await Promise.all([
        sportsApi.getSports(),
        sportsApi.getCountries()
      ]);
      setSports(sportsData);
      setCountries(countriesData);
      await loadLeagues();
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLeagues = async () => {
    try {
      const response = await sportsApi.getLeaguesPaginated(
        selectedSport || undefined,
        selectedCountry || undefined,
        currentPage,
        pageSize,
        false
      );

      setLeagues(response.leagues);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setHasNextPage(response.hasNextPage);
      setHasPreviousPage(response.hasPreviousPage);
    } catch (err) {
      setError('Fehler beim Laden der Ligen');
      console.error('Error loading leagues:', err);
    }
  };

  // CRUD Functions
  const handleCreateLeague = () => {
    setEditingLeague(null);
    setShowForm(true);
  };

  const handleEditLeague = (league: League) => {
    setEditingLeague(league);
    setShowForm(true);
  };

  const handleSaveLeague = async (data: {
    name: string;
    shortName?: string;
    description?: string;
    level?: number;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
  }) => {
    try {
      setFormLoading(true);
      if (editingLeague) {
        await sportsApi.updateLeague(editingLeague.id, data);
        showSuccess('Liga erfolgreich aktualisiert');
      } else {
        // Create new league
        if (!data.sportId || !data.countryId) {
          showError('Sport und Land sind Pflichtfelder');
          return;
        }
        await sportsApi.createLeague({
          name: data.name,
          shortName: data.shortName,
          description: data.description,
          level: data.level,
          sportId: data.sportId,
          countryId: data.countryId
        });
        showSuccess('Liga erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingLeague(null);
      await loadLeagues();
    } catch (err: any) {
      const errorMessage = err.message || (editingLeague ? 'Fehler beim Aktualisieren der Liga' : 'Fehler beim Erstellen der Liga');
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteLeague = async (league: League) => {
    try {
      const { canDelete } = await sportsApi.canDeleteLeague(league.id);
      if (!canDelete) {
        showWarning('Diese Liga kann nicht gelöscht werden, da sie von anderen Datensätzen verwendet wird.');
        return;
      }
      setLeagueToDelete(league);
      setShowDeleteDialog(true);
    } catch (err) {
      showError('Fehler beim Prüfen der Löschberechtigung');
    }
  };

  const confirmDelete = async () => {
    if (!leagueToDelete) return;
    
    try {
      await sportsApi.deleteLeague(leagueToDelete.id);
      showSuccess('Liga erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setLeagueToDelete(null);
      await loadLeagues();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Löschen der Liga');
    }
  };

  const handleBulkSelect = (leagueId: number) => {
    const newSelected = new Set(selectedLeagues);
    if (newSelected.has(leagueId)) {
      newSelected.delete(leagueId);
    } else {
      newSelected.add(leagueId);
    }
    setSelectedLeagues(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedLeagues.size === filteredLeagues.length) {
      setSelectedLeagues(new Set());
    } else {
      setSelectedLeagues(new Set(filteredLeagues.map(l => l.id)));
    }
  };

  // Since we're using server-side pagination, we don't need client-side filtering
  // The filtering is done via API parameters
  const filteredLeagues = leagues;

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Ligen werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler beim Laden der Ligen</h2>
          <p>{error}</p>
          <button onClick={loadData}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="page-container">
        <LeagueForm
          league={editingLeague}
          sports={sports}
          countries={countries}
          onSave={handleSaveLeague}
          onCancel={() => {
            setShowForm(false);
            setEditingLeague(null);
          }}
          loading={formLoading}
        />
        {toast && <Toast {...toast} onClose={() => {}} />}
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateLeague}>
            <Plus size={14} /> Neu
          </button>
          <button
            className={`page-btn ${bulkMode ? 'page-btn-info' : 'page-btn-secondary'}`}
            onClick={() => { setBulkMode(!bulkMode); setSelectedLeagues(new Set()); }}
          >
            {bulkMode ? <><XCircle size={14} /> Beenden</> : <><CheckSquare size={14} /> Auswahl</>}
          </button>
          {bulkMode && selectedLeagues.size > 0 && (
            <button className="page-btn page-btn-danger" onClick={() => showWarning('Bulk-Löschung ist noch nicht implementiert')}>
              <Trash2 size={14} /> Löschen ({selectedLeagues.size})
            </button>
          )}
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Ligen suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={selectedSport || ''}
            onChange={(e) => {
              setSelectedSport(e.target.value ? parseInt(e.target.value) : null);
              setCurrentPage(1);
            }}
          >
            <option value="">Alle Sportarten</option>
            {sports.map(sport => (
              <option key={sport.id} value={sport.id}>
                {sport.icon ? `${sport.icon} ` : ''}{sport.name}
              </option>
            ))}
          </select>
          <select
            className="page-filter-select"
            value={selectedCountry || ''}
            onChange={(e) => {
              setSelectedCountry(e.target.value ? parseInt(e.target.value) : null);
              setCurrentPage(1);
            }}
          >
            <option value="">Alle Länder</option>
            {countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.flag ? `${country.flag} ` : ''}{country.name}
              </option>
            ))}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredLeagues.length} / {totalCount} Ligen</span>
        </div>
      </div>

      {/* Content */}
      {filteredLeagues.length === 0 ? (
        <div className="page-empty">
          <h3>Keine Ligen gefunden</h3>
          <p>Es wurden keine Ligen gefunden, die Ihren Suchkriterien entsprechen.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredLeagues.map(league => (
            <div key={league.id} className="page-card" onClick={() => bulkMode && handleBulkSelect(league.id)}>
              {bulkMode && (
                <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem', zIndex: 1 }}>
                  <input type="checkbox" checked={selectedLeagues.has(league.id)} onChange={() => handleBulkSelect(league.id)} />
                </div>
              )}
              <div className="page-card-header">
                <h3 className="page-card-title">{league.name}</h3>
                <span className={`page-badge ${league.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                  {league.isActive ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <div className="page-card-content">
                {league.shortName && <div className="page-card-row"><strong>Kürzel:</strong> {league.shortName}</div>}
                <div className="page-card-row"><strong>Sportart:</strong> {league.sport?.icon ? `${league.sport.icon} ` : ''}{league.sport?.name || 'Unbekannt'}</div>
                <div className="page-card-row"><strong>Land:</strong> {league.country?.flag ? `${league.country.flag} ` : ''}{league.country?.name || 'Unbekannt'}</div>
                <div className="page-card-row"><strong>Level:</strong> {league.level}</div>
                {league.description && <div className="page-card-row"><strong>Beschreibung:</strong> {league.description}</div>}
              </div>
              {!bulkMode && (
                <div className="page-card-actions">
                  <button className="page-btn-icon edit" onClick={() => handleEditLeague(league)} title="Bearbeiten"><Pencil size={14} /></button>
                  <button className="page-btn-icon delete" onClick={() => handleDeleteLeague(league)} title="Löschen"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                {bulkMode && <th><input type="checkbox" checked={selectedLeagues.size === filteredLeagues.length && filteredLeagues.length > 0} onChange={handleSelectAll} /></th>}
                <th>Name</th>
                <th>Sportart</th>
                <th>Land</th>
                <th>Level</th>
                <th>Status</th>
                {!bulkMode && <th>Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {filteredLeagues.map(league => (
                <tr key={league.id}>
                  {bulkMode && <td><input type="checkbox" checked={selectedLeagues.has(league.id)} onChange={() => handleBulkSelect(league.id)} /></td>}
                  <td>
                    <div style={{ fontWeight: 500 }}>{league.name}</div>
                    {league.shortName && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{league.shortName}</div>}
                  </td>
                  <td>{league.sport?.icon ? `${league.sport.icon} ` : ''}{league.sport?.name || 'Unbekannt'}</td>
                  <td>{league.country?.flag ? `${league.country.flag} ` : ''}{league.country?.name || 'Unbekannt'}</td>
                  <td>{league.level}</td>
                  <td><span className={`page-badge ${league.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>{league.isActive ? 'Aktiv' : 'Inaktiv'}</span></td>
                  {!bulkMode && (
                    <td>
                      <div className="page-table-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditLeague(league)} title="Bearbeiten"><Pencil size={12} /></button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteLeague(league)} title="Löschen"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="page-pagination">
          <button className="page-btn page-btn-secondary" onClick={() => setCurrentPage(1)} disabled={!hasPreviousPage}><ChevronsLeft size={14} /> Erste</button>
          <button className="page-btn page-btn-secondary" onClick={() => setCurrentPage(currentPage - 1)} disabled={!hasPreviousPage}><ChevronLeft size={14} /> Zurück</button>
          <span className="page-pagination-info">Seite {currentPage} von {totalPages} ({totalCount} Ligen)</span>
          <button className="page-btn page-btn-secondary" onClick={() => setCurrentPage(currentPage + 1)} disabled={!hasNextPage}>Weiter <ChevronRight size={14} /></button>
          <button className="page-btn page-btn-secondary" onClick={() => setCurrentPage(totalPages)} disabled={!hasNextPage}>Letzte <ChevronsRight size={14} /></button>
          <select className="page-filter-select" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}>
            <option value={10}>10 pro Seite</option>
            <option value={20}>20 pro Seite</option>
            <option value={50}>50 pro Seite</option>
            <option value={100}>100 pro Seite</option>
          </select>
        </div>
      )}

      {/* Dialogs */}
      {showDeleteDialog && leagueToDelete && (
        <ConfirmDialog
          isOpen={showDeleteDialog}
          title="Liga löschen"
          message={`Möchten Sie die Liga "${leagueToDelete.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          onConfirm={confirmDelete}
          onCancel={() => { setShowDeleteDialog(false); setLeagueToDelete(null); }}
          type="danger"
        />
      )}

      {toast && <Toast {...toast} onClose={() => {}} />}
    </div>
  );
};

export default Leagues;
